import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addTenderRound,
  createClientBranch,
  createClientSubBranch,
  approvePricingFunction,
  createPricingFunction,
  createPricingPreset,
  createPricingProposal,
  createPricingTemplate,
  createTender,
  deletePricingFunction,
  deletePricingPreset,
  deletePricingTemplate,
  deleteTender,
  deleteTenderAttachment,
  deleteTenderMaterial,
  fetchClientBranches,
  fetchClientSubBranches,
  fetchMyPricingNotifications,
  fetchPricingFunctions,
  fetchPricingPresets,
  fetchPricingTemplates,
  fetchSimilarProposals,
  fetchTender,
  fetchTenders,
  markPricingNotificationRead,
  togglePricingNotificationPin,
  markTenderMessage,
  markTenderWon,
  postTenderMessage,
  rejectPricingFunction,
  resolvePricingPreset,
  reviewPricingProposal,
  revokePricingFunction,
  sendPricingNotification,
  setTenderStatus,
  submitTenderOffer,
  withdrawTenderOffer,
  updatePricingFunction,
  updatePricingPreset,
  updatePricingProposal,
  updatePricingTemplate,
  updateTender,
  updateTenderRound,
  uploadTenderAttachment,
  uploadTenderMaterial,
  watchTender,
  unwatchTender,
  claimTenderPricing,
  releaseTenderPricing,
  fetchPendingApprovalsCount,
  fetchTenderActionCounts,
  deleteTenderMessage,
  startTenderConstruction,
  fetchScopePrefixes,
  createScopePrefix,
  updateScopePrefix,
  deleteScopePrefix,
} from "../api/tenders";
import type {
  MarkTenderWonFormData,
  PricingItemTemplateFormData,
  PricingPresetFormData,
  PricingPresetFunctionFormData,
  TenderFormData,
  TenderOfferFormData,
  TenderPricingProposalFormData,
  TenderScopePrefixFormData,
} from "../types";


export function useClientBranches(clientId: string | undefined) {
  return useQuery({
    queryKey: ["client-branches", clientId],
    queryFn: () => fetchClientBranches(clientId!),
    enabled: Boolean(clientId),
  });
}

export function useCreateClientBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClientBranch,
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["client-branches", vars.client] }),
  });
}

export function useClientSubBranches(branchId: string | undefined) {
  return useQuery({
    queryKey: ["client-sub-branches", branchId],
    queryFn: () => fetchClientSubBranches(branchId!),
    enabled: Boolean(branchId),
  });
}

export function useCreateClientSubBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createClientSubBranch,
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ["client-sub-branches", vars.branch] }),
  });
}

export function useSimilarProposals(tenderId: string, templateIds: string[], enabled: boolean) {
  return useQuery({
    queryKey: ["similar-proposals", tenderId, templateIds.sort().join(",")],
    queryFn: () => fetchSimilarProposals(tenderId, templateIds),
    enabled: enabled && Boolean(tenderId),
    staleTime: 5 * 60_000,
  });
}

export function useTenders(params?: { status?: string; client?: string }) {
  return useQuery({
    queryKey: ["tenders", params],
    queryFn: () => fetchTenders(params),
  });
}

export function useTender(id: string) {
  return useQuery({
    queryKey: ["tenders", id],
    queryFn: () => fetchTender(id),
    enabled: Boolean(id),
  });
}

export function useCreateTender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TenderFormData) => createTender(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders"] }),
  });
}

export function useUpdateTender(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TenderFormData>) => updateTender(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenders", id] });
      qc.invalidateQueries({ queryKey: ["tenders"] });
    },
  });
}

export function useDeleteTender() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTender(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders"] }),
  });
}

export function useSetTenderStatus(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (status: "open" | "won_unofficial" | "lost" | "cancelled" | "not_starting") => setTenderStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenders", id] });
      qc.invalidateQueries({ queryKey: ["tenders"] });
    },
  });
}

export function usePendingApprovalsCount(enabled = true) {
  return useQuery({
    queryKey: ["tenders", "pending-approvals-count"],
    queryFn: fetchPendingApprovalsCount,
    enabled,
  });
}

export function useTenderActionCounts(enabled = true) {
  return useQuery({
    queryKey: ["tenders", "action-counts"],
    queryFn: fetchTenderActionCounts,
    enabled,
  });
}

export function useWatchTender(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (watching: boolean) => watching ? watchTender(tenderId) : unwatchTender(tenderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

/** Przejmij / zwolnij wyliczanie wyceny aktywnej tury. Odświeża detal, listy
 *  (chip „Przejęte (…)") i liczniki chipów, także w portfelu ZE (ten sam PK). */
export function useClaimTenderPricing(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (arg: { action: "claim"; force?: boolean } | { action: "release" }) =>
      arg.action === "claim" ? claimTenderPricing(tenderId, arg.force) : releaseTenderPricing(tenderId),
    onSuccess: (tender) => {
      qc.setQueryData(["tenders", tenderId], tender);
      qc.invalidateQueries({ queryKey: ["tenders"] });
      qc.invalidateQueries({ queryKey: ["zt"] });
    },
  });
}

export function useAddTenderRound(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { offer_deadline?: string; notes?: string }) => addTenderRound(tenderId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useUpdateTenderRound(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId, data }: { roundId: string; data: { offer_deadline?: string; status?: string; notes?: string } }) =>
      updateTenderRound(tenderId, roundId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useUploadTenderAttachment(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name }: { file: File; name: string }) => uploadTenderAttachment(tenderId, file, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useDeleteTenderAttachment(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => deleteTenderAttachment(tenderId, attachmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useUploadTenderMaterial(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, name }: { file: File; name: string }) => uploadTenderMaterial(tenderId, file, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useDeleteTenderMaterial(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (materialId: string) => deleteTenderMaterial(tenderId, materialId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function usePostTenderMessage(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string; parent?: string; files?: File[]; mentionIds?: string[] }) =>
      postTenderMessage(tenderId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useMarkTenderMessage(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, is_marked, mark_type }: { messageId: string; is_marked: boolean; mark_type?: string | null }) =>
      markTenderMessage(tenderId, messageId, is_marked, mark_type),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useDeleteTenderMessage(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => deleteTenderMessage(tenderId, messageId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useSubmitTenderOffer(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId, data }: { roundId: string; data: TenderOfferFormData }) =>
      submitTenderOffer(tenderId, roundId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useWithdrawTenderOffer(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId, offerId }: { roundId: string; offerId: string }) =>
      withdrawTenderOffer(tenderId, roundId, offerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useCreatePricingProposal(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId, data }: { roundId: string; data: TenderPricingProposalFormData }) =>
      createPricingProposal(tenderId, roundId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useSendPricingNotification(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      proposalId,
      recipient_ids,
      message,
    }: {
      roundId: string;
      proposalId: string;
      recipient_ids: string[];
      message: string;
    }) => sendPricingNotification(tenderId, roundId, proposalId, recipient_ids, message),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useUpdatePricingProposal(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      proposalId,
      data,
    }: {
      roundId: string;
      proposalId: string;
      data: TenderPricingProposalFormData;
    }) => updatePricingProposal(tenderId, roundId, proposalId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useReviewPricingProposal(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      proposalId,
      action,
    }: {
      roundId: string;
      proposalId: string;
      action: "accept" | "reject";
    }) => reviewPricingProposal(tenderId, roundId, proposalId, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tenders", tenderId] }),
  });
}

export function useMarkTenderWon(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MarkTenderWonFormData) => markTenderWon(tenderId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenders", tenderId] });
      qc.invalidateQueries({ queryKey: ["tenders"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

export function useMyPricingNotifications(unread?: boolean, enabled = true) {
  return useQuery({
    queryKey: ["pricing-notifications", unread],
    queryFn: () => fetchMyPricingNotifications(unread),
    // Brak refetchInterval — polling przejął useNotificationSync (leader election).
    enabled,
  });
}

export function useMarkPricingNotificationRead() {
  const qc = useQueryClient();
  // Import dynamiczny żeby uniknąć circular dependency
  // (tenders → tasks/hooks byłby odwrotny do istniejącego tasks → tenders)
  return useMutation({
    mutationFn: (id: string) => markPricingNotificationRead(id),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["pricing-notifications"] });
      const { broadcastRefreshRequest } = await import("@/features/tasks/hooks/useNotificationSync");
      broadcastRefreshRequest();
    },
  });
}

export function useTogglePricingPin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => togglePricingNotificationPin(id),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["pricing-notifications"] });
      const { broadcastRefreshRequest } = await import("@/features/tasks/hooks/useNotificationSync");
      broadcastRefreshRequest();
    },
  });
}

// ── Pricing catalog — templates ───────────────────────────────────────────

export function usePricingTemplates(params?: { client?: string; active?: string }) {
  return useQuery({
    queryKey: ["pricing-templates", params],
    queryFn: () => fetchPricingTemplates(params),
  });
}

export function useCreatePricingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PricingItemTemplateFormData) => createPricingTemplate(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-templates"] }),
  });
}

export function useUpdatePricingTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PricingItemTemplateFormData>) => updatePricingTemplate(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-templates"] }),
  });
}

export function useDeletePricingTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePricingTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-templates"] }),
  });
}

// ── Pricing catalog — presets ─────────────────────────────────────────────

export function usePricingPresets(params?: { client?: string }) {
  return useQuery({
    queryKey: ["pricing-presets", params],
    queryFn: () => fetchPricingPresets(params),
  });
}

export function useCreatePricingPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PricingPresetFormData) => createPricingPreset(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-presets"] }),
  });
}

export function useUpdatePricingPreset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PricingPresetFormData) => updatePricingPreset(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-presets"] }),
  });
}

export function useDeletePricingPreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePricingPreset(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-presets"] }),
  });
}

export function useResolvePricingPreset() {
  return useMutation({
    mutationFn: ({ id, parameters }: { id: string; parameters: Record<string, number> }) =>
      resolvePricingPreset(id, parameters),
  });
}

// ── Pricing catalog — preset functions ────────────────────────────────────

export function usePricingFunctions(params?: { search?: string; status?: string }) {
  return useQuery({
    queryKey: ["pricing-functions", params],
    queryFn: () => fetchPricingFunctions(params),
  });
}

export function useReviewPricingFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" | "revoke" }) =>
      action === "approve"
        ? approvePricingFunction(id)
        : action === "reject"
          ? rejectPricingFunction(id)
          : revokePricingFunction(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-functions"] }),
  });
}

export function useCreatePricingFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: PricingPresetFunctionFormData) => createPricingFunction(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-functions"] }),
  });
}

export function useUpdatePricingFunction(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<PricingPresetFunctionFormData>) => updatePricingFunction(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-functions"] }),
  });
}

export function useDeletePricingFunction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePricingFunction(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["pricing-functions"] }),
  });
}

// ── Zakres przetargu — mapowanie prefiksów nazw ──────────────────────────────

const SCOPE_PREFIX_QK = ["tender-scope-prefixes"];

export function useScopePrefixes(params?: { active?: string }) {
  return useQuery({
    queryKey: [...SCOPE_PREFIX_QK, params],
    queryFn: () => fetchScopePrefixes(params),
  });
}

export function useCreateScopePrefix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: TenderScopePrefixFormData) => createScopePrefix(data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCOPE_PREFIX_QK }),
  });
}

export function useUpdateScopePrefix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TenderScopePrefixFormData> }) =>
      updateScopePrefix(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCOPE_PREFIX_QK }),
  });
}

export function useDeleteScopePrefix() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteScopePrefix(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: SCOPE_PREFIX_QK }),
  });
}

export function useStartTenderConstruction(tenderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data?: { name?: string; short_name?: string }) =>
      startTenderConstruction(tenderId, data ?? {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tenders", tenderId] });
      qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
